import coverApache from "@/assets/cover-apache.png";
import coverEasmComparison from "@/assets/cover-easm-comparison.png";
import coverWafCdn from "@/assets/cover-waf-cdn.png";
import coverNginx from "@/assets/cover-nginx.png";
import coverRemoteAccess from "@/assets/cover-remote-access.png";
import coverJumpServer from "@/assets/cover-jump-server.png";
import coverTlsMtls from "@/assets/cover-tls-mtls.png";
import coverSpfDkimDmarc from "@/assets/cover-spf-dkim-dmarc.png";
import type { Language } from "@/i18n/translations";

export interface Article {
  slug: string;
  title: Record<Language, string>;
  excerpt: Record<Language, string>;
  date: string;
  readTime: string;
  category: string;
  tags: Record<Language, string[]>;
  coverGradient: string;
  coverImage: string;
  ogImage: string;
  markdownPath: Record<Language, string>;
}

export const articles: Article[] = [
  {
    slug: "hardening-apache-completo",
    title: {
      pt: "Hardening Apache: Guia Completo de Segurança",
      es: "Hardening de Apache: Guía Completa",
      en: "Apache Hardening: Complete Guide",
    },
    excerpt: {
      pt: "Cada configuração padrão do Apache que você ignora é uma porta entreaberta. Este guia mostra o que cada uma significa — e como fechar.",
      es: "Cada configuración predeterminada de Apache que ignora es una puerta entreabierta. Esta guía muestra qué significa cada una — y cómo cerrarla.",
      en: "Every default Apache setting you ignore is a door left ajar. This guide shows what each one means — and how to close it.",
    },
    date: "2025-07-10",
    readTime: "25 min",
    category: "Hardening",
    tags: {
      pt: ["Apache", "Segurança", "Servidor Web", "CVE"],
      es: ["Apache", "Seguridad", "Servidor Web", "CVE"],
      en: ["Apache", "Security", "Web Server", "CVE"],
    },
    coverGradient: "from-orange-500/20 to-red-600/20",
    coverImage: coverApache,
    ogImage: "/og/cover-apache.png",
    markdownPath: {
      pt: "/articles/hardening-apache-completo.md",
      es: "/articles/hardening-apache-completo-es.md",
      en: "/articles/hardening-apache-completo-en.md",
    },
  },
  {
    slug: "hardening-nginx-completo",
    title: {
      pt: "Hardening Nginx: Guia Completo de Segurança",
      es: "Hardening de Nginx: Guía Completa",
      en: "Nginx Hardening: Complete Guide",
    },
    excerpt: {
      pt: "Cada linha de configuração padrão do Nginx que você ignora é uma porta entreaberta. Este guia mostra o que cada uma significa — e como fechar.",
      es: "Cada línea de configuración predeterminada de Nginx que ignora es una puerta entreabierta. Esta guía muestra qué significa cada una — y cómo cerrarla.",
      en: "Every default Nginx setting you ignore is a door left ajar. This guide shows what each one means — and how to close it.",
    },
    date: "2025-07-12",
    readTime: "24 min",
    category: "Hardening",
    tags: {
      pt: ["Nginx", "Segurança", "Servidor Web", "CVE"],
      es: ["Nginx", "Seguridad", "Servidor Web", "CVE"],
      en: ["Nginx", "Security", "Web Server", "CVE"],
    },
    coverGradient: "from-emerald-500/20 to-cyan-600/20",
    coverImage: coverNginx,
    ogImage: "/og/cover-nginx.png",
    markdownPath: {
      pt: "/articles/hardening-nginx-completo.md",
      es: "/articles/hardening-nginx-completo-es.md",
      en: "/articles/hardening-nginx-completo-en.md",
    },
  },
  {
    slug: "acesso-remoto-seguro",
    title: {
      pt: "Acesso remoto seguro: SSH, RDP, VPN e Zero Trust",
      es: "Acceso Remoto Seguro: SSH, RDP y VPN",
      en: "Secure Remote Access: SSH, RDP and VPN",
    },
    excerpt: {
      pt: "Por que deixar a porta 3389 aberta na internet é o equivalente digital de deixar a chave de casa embaixo do tapete — e o que fazer no lugar disso.",
      es: "Por qué dejar el puerto 3389 abierto en internet es el equivalente digital de dejar la llave debajo del felpudo — y qué hacer en su lugar.",
      en: "Why leaving port 3389 open on the internet is the digital equivalent of leaving your house key under the doormat — and what to do instead.",
    },
    date: "2025-07-15",
    readTime: "30 min",
    category: "Remote Access",
    tags: {
      pt: ["SSH", "RDP", "VPN", "Zero Trust"],
      es: ["SSH", "RDP", "VPN", "Zero Trust"],
      en: ["SSH", "RDP", "VPN", "Zero Trust"],
    },
    coverGradient: "from-blue-500/20 to-indigo-600/20",
    coverImage: coverRemoteAccess,
    ogImage: "/og/cover-remote-access.png",
    markdownPath: {
      pt: "/articles/acesso-remoto-seguro.md",
      es: "/articles/acesso-remoto-seguro-es.md",
      en: "/articles/acesso-remoto-seguro-en.md",
    },
  },
  {
    slug: "jump-server-guia-definitivo",
    title: {
      pt: "Jump Server: Guia de Segurança e Boas Práticas",
      es: "Jump Server: Guía de Seguridad",
      en: "Jump Server: Security Guide",
    },
    excerpt: {
      pt: "Como um servidor dedicado pode organizar seu suporte ou virar a próxima porta de entrada para ransomware.",
      es: "Cómo un servidor dedicado puede organizar tu soporte o convertirse en la próxima puerta de entrada para ransomware.",
      en: "How a dedicated server can organize your support or become the next ransomware entry point.",
    },
    date: "2025-07-18",
    readTime: "28 min",
    category: "Infrastructure",
    tags: {
      pt: ["Jump Server", "Bastion Host", "SSH", "Segurança"],
      es: ["Jump Server", "Bastion Host", "SSH", "Seguridad"],
      en: ["Jump Server", "Bastion Host", "SSH", "Security"],
    },
    coverGradient: "from-purple-500/20 to-violet-600/20",
    coverImage: coverJumpServer,
    ogImage: "/og/cover-jump-server.png",
    markdownPath: {
      pt: "/articles/jump-server-guia-definitivo.md",
      es: "/articles/jump-server-guia-definitivo-es.md",
      en: "/articles/jump-server-guia-definitivo-en.md",
    },
  },
  {
    slug: "certificados-tls-mtls-guia-definitivo",
    title: {
      pt: "Certificados TLS e mTLS: O Guia Definitivo",
      es: "Certificados TLS y mTLS: La Guía Definitiva",
      en: "TLS & mTLS Certificates: The Definitive Guide",
    },
    excerpt: {
      pt: "Tudo que pode dar errado com certificados TLS — e por que mTLS está virando o padrão pra coisa séria em 2026.",
      es: "Todo lo que puede salir mal con certificados TLS — y por qué mTLS se está convirtiendo en el estándar para cosas serias en 2026.",
      en: "Everything that can go wrong with TLS certificates — and why mTLS is becoming the standard for serious stuff in 2026.",
    },
    date: "2025-07-22",
    readTime: "32 min",
    category: "Encryption",
    tags: {
      pt: ["TLS", "mTLS", "Certificados", "HTTPS"],
      es: ["TLS", "mTLS", "Certificados", "HTTPS"],
      en: ["TLS", "mTLS", "Certificates", "HTTPS"],
    },
    coverGradient: "from-teal-500/20 to-cyan-600/20",
    coverImage: coverTlsMtls,
    ogImage: "/og/cover-tls-mtls.png",
    markdownPath: {
      pt: "/articles/certificados-tls-mtls-guia-definitivo.md",
      es: "/articles/certificados-tls-mtls-guia-definitivo-es.md",
      en: "/articles/certificados-tls-mtls-guia-definitivo-en.md",
    },
  },
  {
    slug: "spf-dkim-dmarc-guia-definitivo",
    title: {
      pt: "SPF, DKIM e DMARC: Seu E-mail Vai Pro Spam",
      es: "SPF, DKIM y DMARC: Tu Email Va al Spam",
      en: "SPF, DKIM & DMARC: Why Your Email Goes to Spam",
    },
    excerpt: {
      pt: "Os três protocolos que separam seu e-mail legítimo da caixa de spam — e sua marca de ser usada por golpistas.",
      es: "Los tres protocolos que separan tu email legítimo de la bandeja de spam — y tu marca de ser usada por estafadores.",
      en: "The three protocols that separate your legitimate email from the spam folder — and your brand from being used by scammers.",
    },
    date: "2025-07-20",
    readTime: "25 min",
    category: "Email Security",
    tags: {
      pt: ["SPF", "DKIM", "DMARC", "E-mail"],
      es: ["SPF", "DKIM", "DMARC", "Correo"],
      en: ["SPF", "DKIM", "DMARC", "Email"],
    },
    coverGradient: "from-amber-500/20 to-orange-600/20",
    coverImage: coverSpfDkimDmarc,
    ogImage: "/og/cover-spf-dkim-dmarc.png",
    markdownPath: {
      pt: "/articles/spf-dkim-dmarc-guia-definitivo.md",
      es: "/articles/spf-dkim-dmarc-guia-definitivo-es.md",
      en: "/articles/spf-dkim-dmarc-guia-definitivo-en.md",
    },
  },
  {
    slug: "waf-cdn-guia-definitivo",
    title: {
      pt: "WAF e CDN: O Guia para Amplificar sua Segurança",
      es: "WAF y CDN: Guía para Amplificar tu Seguridad",
      en: "WAF & CDN: The Guide to Amplify Your Security",
    },
    excerpt: {
      pt: "Guia honesto sobre WAF e CDN — por que muitas empresas usam errado, gastam demais ou ficam desprotegidas.",
      es: "Guía honesta sobre WAF y CDN: por qué tantas empresas las usan mal, gastan de más o quedan desprotegidas.",
      en: "Honest guide to WAF and CDN — why many companies use them wrong, overspend, or stay unprotected.",
    },
    date: "2025-07-25",
    readTime: "30 min",
    category: "Web Security",
    tags: {
      pt: ["WAF", "CDN", "Cloudflare", "Segurança Web"],
      es: ["WAF", "CDN", "Cloudflare", "Seguridad Web"],
      en: ["WAF", "CDN", "Cloudflare", "Web Security"],
    },
    coverGradient: "from-sky-500/20 to-blue-700/20",
    coverImage: coverWafCdn,
    ogImage: "/og/cover-waf-cdn.png",
    markdownPath: {
      pt: "/articles/waf-cdn-guia-definitivo.md",
      es: "/articles/waf-cdn-guia-definitivo-es.md",
      en: "/articles/waf-cdn-guia-definitivo-en.md",
    },
  },
  {
    slug: "easm-tools-comparison",
    title: {
      pt: "Ferramentas de Attack Surface Management: Comparativo",
      es: "Herramientas de Attack Surface Management: Comparativo",
      en: "Attack Surface Management Tools: A Market Comparison",
    },
    excerpt: {
      pt: "Comparativo honesto de EASM: abordagens, preços, limitações e por que a monitoração contínua sem agente separa uma plataforma séria de uma planilha bonita.",
      es: "Comparativo honesto de EASM: enfoques, precios, limitaciones y por qué el monitoreo continuo sin agente separa una plataforma seria de una hoja de cálculo bonita.",
      en: "Honest EASM comparison: approaches, pricing, limitations, and why continuous agentless monitoring separates a serious platform from a pretty spreadsheet.",
    },
    date: "2026-07-20",
    readTime: "28 min",
    category: "EASM",
    tags: {
      pt: ["EASM", "Attack Surface", "Comparativo", "Cibersegurança"],
      es: ["EASM", "Attack Surface", "Comparativo", "Ciberseguridad"],
      en: ["EASM", "Attack Surface", "Comparison", "Cybersecurity"],
    },
    coverGradient: "from-cyan-500/20 to-blue-600/20",
    coverImage: coverEasmComparison,
    ogImage: "/og/cover-easm-comparison.png",
    markdownPath: {
      pt: "/articles/easm-tools-comparison.md",
      es: "/articles/easm-tools-comparison-es.md",
      en: "/articles/easm-tools-comparison-en.md",
    },
  },
];
